import React, { useState } from 'react';
import { Check, UserCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useUserAvatar } from '../../hooks/useUserAvatar';
import { AVATARS, avatarLabel } from '../layout/AvatarPicker';
import ProfileLayout from './ProfileLayout';

// The Account section of Settings: who you are and how you look in the sidebar. The Figma plugin
// token is its own section -- a credential and an avatar have nothing to do with each other, and
// stacking them on one page made the page read as a pile rather than a setting.
export default function UserProfilePage() {
  const { user } = useAuth();
  const { avatar, setAvatar } = useUserAvatar();
  const [hovered, setHovered] = useState(null);
  const noAvatarSelected = !avatar;

  return (
    <ProfileLayout>
      {/* User info */}
      <div className="flex items-center gap-4 p-4 rounded-xl bg-surface-2 border border-white/10">
        <div className="w-12 h-12 rounded-full overflow-hidden bg-surface-1 ring-2 ring-white/10 flex items-center justify-center shrink-0">
          {avatar
            ? <img src={`/avatars/${avatar}.png`} alt={user?.name} className="w-full h-full object-cover" />
            : <UserCircle className="w-7 h-7 text-ink-dim" />
          }
        </div>
        <div>
          <p className="text-sm font-bold text-ink">{user?.name}</p>
          <p className="text-xs text-ink-dim mt-0.5">{avatar ? avatarLabel(avatar) : 'No avatar'}</p>
        </div>
      </div>

      {/* Avatar picker */}
      <div>
        <h2 className="text-sm font-bold text-ink mb-1">Choose avatar</h2>
        <p className="text-xs text-ink-dim mb-4 h-4">
          {hovered === '__none__' ? 'No avatar' : hovered ? avatarLabel(hovered) : ''}
        </p>
        <div className="grid grid-cols-9 gap-2">
          {/* No avatar tile */}
          <button
            title="No avatar"
            onClick={() => setAvatar(null)}
            onMouseEnter={() => setHovered('__none__')}
            onMouseLeave={() => setHovered(null)}
            className={`
              relative rounded-lg overflow-hidden aspect-square transition-all duration-150 hover:scale-105 flex items-center justify-center bg-surface-2
              ${noAvatarSelected ? 'ring-2 ring-primary-500 ring-offset-2 ring-offset-surface-1' : 'ring-1 ring-white/10 hover:ring-white/30'}
            `}
          >
            <UserCircle className="w-3/5 h-3/5 text-ink-dim" />
            {noAvatarSelected && (
              <div className="absolute inset-0 bg-primary-500/20 flex items-end justify-end p-1">
                <Check className="h-3 w-3 text-primary-400" />
              </div>
            )}
          </button>

          {AVATARS.map(slug => {
            const isSelected = avatar === slug;
            return (
              <button
                key={slug}
                title={avatarLabel(slug)}
                onClick={() => setAvatar(slug)}
                onMouseEnter={() => setHovered(slug)}
                onMouseLeave={() => setHovered(null)}
                className={`
                  relative rounded-lg overflow-hidden aspect-square transition-all duration-150 hover:scale-105
                  ${isSelected ? 'ring-2 ring-primary-500 ring-offset-2 ring-offset-surface-1' : 'ring-1 ring-white/10 hover:ring-white/30'}
                `}
              >
                <img
                  src={`/avatars/${slug}.png`}
                  alt={avatarLabel(slug)}
                  className="w-full h-full object-cover"
                />
                {isSelected && (
                  <div className="absolute inset-0 bg-primary-500/20 flex items-end justify-end p-1">
                    <Check className="h-3 w-3 text-primary-400" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

    </ProfileLayout>
  );
}
